import { readConsent, writeConsent, type Consent, type StorageLike } from "./consent";
import type { Moment } from "./moments";
import { screenFor } from "./screens";
import type { Sink } from "./sink";

export type { Consent } from "./consent";
export type { Moment } from "./moments";

/**
 * The one analytics service the app talks to.
 *
 * It holds the visitor's choice and their identity, and passes every moment
 * to each tool behind it (Google Analytics for the ads, PostHog for how the
 * app is used). A screen never imports a vendor SDK; it names a moment.
 */
export interface Analytics {
  readonly enabled: boolean;
  track(moment: Moment): void;
  /** The visitor's stored choice, or null when they have not chosen. */
  consent(): Consent | null;
  setConsent(granted: boolean): void;
  identify(userId: string): void;
  forget(): void;
  /** The router moved to `pathname`. */
  screen(pathname: string): void;
}

export function createAnalytics(sinks: readonly Sink[], storage: StorageLike | null): Analytics {
  // A failing tool must never take a screen down with it.
  function each(action: (sink: Sink) => void): void {
    for (const sink of sinks) {
      try {
        action(sink);
      } catch (error) {
        console.warn("Analytics call failed.", error);
      }
    }
  }

  return {
    enabled: sinks.length > 0,
    track: (moment) => each((sink) => sink.track(moment)),
    consent: () => readConsent(storage),
    setConsent(granted) {
      const choice: Consent = granted ? "granted" : "denied";
      writeConsent(storage, choice);
      each((sink) => sink.setConsent(choice));
    },
    identify: (userId) => each((sink) => sink.identify(userId)),
    forget: () => each((sink) => sink.forget()),
    screen(pathname) {
      const screen = screenFor(pathname);
      each((sink) => sink.screen(screen));
    },
  };
}

export const noopAnalytics: Analytics = Object.freeze({
  enabled: false,
  track: () => undefined,
  consent: () => null,
  setConsent: () => undefined,
  identify: () => undefined,
  forget: () => undefined,
  screen: () => undefined,
});
